import { useEffect, useState } from "react";
import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, MapPin, Calendar, Gauge, Users, ArrowRight } from "lucide-react";
import {
  fyndanuListingsQuery,
  blocketVehiclesQuery,
  formatPrice,
  formatMil,
  dedupeBlocketAgainstFyndanu,
} from "@/lib/vehicles";
import { imgSrcSet, CARD_SIZES } from "@/lib/img";
import { FyndanuTrustRow } from "@/components/FyndanuTrustRow";
import { DeliveryChecks } from "@/components/DeliveryChecks";


// Re-export for backward compat with existing routes
export { fyndanuListingsQuery };

type Props = {
  /** Om satt: visa max N fordon och en "Visa alla"-knapp. */
  initialLimit?: number;
  /** Dölj den inbyggda trust-raden (används när sidan redan har en). */
  hideTrustRow?: boolean;
  /** Rotera fönstret av synliga fordon var N:e ms (kräver initialLimit). */
  rotateMs?: number;
  /** Lägg guldram runt korten. */
  goldFrame?: boolean;
};

import type { FyndanuListing } from "@/lib/fyndanu.functions";

type UnifiedItem =
  | { kind: "fyndanu"; f: FyndanuListing }
  | { kind: "blocket"; b: import("@/lib/vehicles").BlocketVehicleCard };

export function FyndanuGrid({ initialLimit, hideTrustRow, rotateMs, goldFrame }: Props = {}) {
  const { data } = useSuspenseQuery(fyndanuListingsQuery);
  const blocket = useQuery(blocketVehiclesQuery);
  const fyndanuItems = data.items;
  // Samma fordon ligger både på Fyndanu och Blocket – visa bara en gång.
  const blocketItems = dedupeBlocketAgainstFyndanu(fyndanuItems, blocket.data?.items ?? []);

  // Merge: fyndanu first, then blocket. Sold-marked blocket last.
  const items: UnifiedItem[] = [
    ...fyndanuItems.map((f): UnifiedItem => ({ kind: "fyndanu", f })),
    ...blocketItems
      .slice()
      .sort((a, b) => Number(a.is_sold) - Number(b.is_sold))
      .map((b): UnifiedItem => ({ kind: "blocket", b })),
  ];

  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!rotateMs || !initialLimit || items.length <= initialLimit) return;
    const t = setInterval(() => {
      setOffset((o) => (o + initialLimit) % items.length);
    }, rotateMs);
    return () => clearInterval(t);
  }, [rotateMs, initialLimit, items.length]);



  if (data.error || items.length === 0) {
    return (
      <div className="rounded-3xl border border-border bg-card p-10 text-center">
        <p className="text-muted-foreground">
          Kunde inte visa aktuella fordon just nu.{" "}
          <a
            href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent underline underline-offset-4"
          >
            Se hela lagret på Fyndanu
          </a>
        </p>
      </div>
    );
  }

  let limited: UnifiedItem[] = items;
  if (initialLimit) {
    if (rotateMs && items.length > initialLimit) {
      limited = Array.from({ length: initialLimit }, (_, i) => items[(offset + i) % items.length]);
    } else {
      limited = items.slice(0, initialLimit);
    }
  }
  const hiddenCount = Math.max(0, items.length - (initialLimit ?? items.length));

  const cardClass = goldFrame
    ? "group relative flex flex-col overflow-hidden rounded-3xl border-2 border-[#c9a84c]/70 bg-card shadow-[0_0_0_1px_rgba(201,168,76,0.25),0_18px_50px_-25px_rgba(139,109,32,0.55)] ring-1 ring-[#f0d98a]/40 transition hover:-translate-y-1 hover:border-[#c9a84c] hover:shadow-[0_0_0_1px_rgba(201,168,76,0.45),0_25px_60px_-20px_rgba(139,109,32,0.7)]"
    : "group relative flex flex-col overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-xl";


  return (
    <>
      {!hideTrustRow && <FyndanuTrustRow />}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {limited.map((entry, idx) => {
          const eager = idx < 3;

          if (entry.kind === "blocket") {
            const b = entry.b;
            return (
              <a
                key={`b-${b.external_id}-${offset}-${idx}`}
                href={b.listing_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${cardClass} relative`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
                  {b.image_url && (
                    <img
                      src={b.image_url}
                      srcSet={imgSrcSet(b.image_url)}
                      sizes={CARD_SIZES}
                      alt={b.title ?? "Fordon"}
                      loading={eager ? "eager" : "lazy"}
                      decoding={eager ? "sync" : "async"}
                      className={`h-full w-full object-cover transition duration-700 group-hover:scale-105 ${b.is_sold ? "opacity-70 grayscale-[35%]" : ""}`}
                      style={{ filter: "contrast(1.05) saturate(1.08)" }}
                    />

                  )}
                  <div className="absolute right-3 top-3 rounded-full bg-background/95 px-3 py-1 text-xs font-semibold shadow-sm backdrop-blur">
                    {formatPrice(b.price)}
                  </div>
                  <div className="absolute left-3 top-3 rounded-full bg-[#0057b8]/95 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-sm backdrop-blur">
                    Blocket
                  </div>
                  {b.is_sold && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className="rotate-[-8deg] rounded-md border-2 border-red-600 bg-red-600/90 px-6 py-2 text-2xl font-black uppercase tracking-widest text-white shadow-lg">
                        Såld
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-display text-xl leading-tight line-clamp-2 group-hover:text-accent">
                    {b.title ?? "Fordon"}
                  </h3>
                  <div className="mt-auto pt-4 flex items-center justify-between text-sm text-muted-foreground">
                    <span className="text-xs uppercase tracking-wider">Annonseras på Blocket</span>
                    <span className="inline-flex items-center gap-1 text-accent font-medium">
                      Öppna <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </a>
            );
          }

          const item = entry.f;
          const mil = formatMil(item.mileage);
          return (
            <a
              key={`${item.id}-${offset}-${idx}`}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className={cardClass}
            >


              <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
                <img
                  src={item.image}
                  srcSet={imgSrcSet(item.image)}
                  sizes={CARD_SIZES}
                  alt={item.title}
                  loading={eager ? "eager" : "lazy"}
                  decoding={eager ? "sync" : "async"}
                  className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                  style={{ filter: "contrast(1.05) saturate(1.08)" }}
                />

                <div className="absolute right-3 top-3 rounded-full bg-background/95 px-3 py-1 text-xs font-semibold shadow-sm backdrop-blur">
                  {formatPrice(item.price)}
                </div>
                {item.category !== "annat" && (
                  <div className="absolute left-3 top-3 rounded-full bg-primary/95 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground shadow-sm backdrop-blur">
                    {item.category}
                  </div>
                )}
                {(item.category === "husbil" || item.category === "husvagn") && (
                  <div className="absolute left-3 bottom-3 rounded-full bg-background/95 px-2 py-1 shadow-sm backdrop-blur">
                    <DeliveryChecks compact />
                  </div>
                )}
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-display text-xl leading-tight line-clamp-2 group-hover:text-accent">
                  {item.title}
                </h3>
                <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {item.year && (
                    <div className="inline-flex items-center gap-1">
                      <Calendar size={12} /> {item.year}
                    </div>
                  )}
                  {mil && (
                    <div className="inline-flex items-center gap-1">
                      <Gauge size={12} /> {mil}
                    </div>
                  )}
                  {item.sleepingSpots != null && (
                    <div className="inline-flex items-center gap-1">
                      <Users size={12} /> {item.sleepingSpots} bäddar
                    </div>
                  )}
                </dl>
                <div className="mt-auto pt-4 flex items-center justify-between text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    {item.location && (
                      <>
                        <MapPin size={14} /> {item.location}
                      </>
                    )}
                  </span>
                  <span className="inline-flex items-center gap-1 text-accent font-medium">
                    Visa <ArrowUpRight size={14} />
                  </span>
                </div>
              </div>
            </a>

          );
        })}
      </div>

      {hiddenCount > 0 && (
        <div className="mt-10 flex flex-col items-center gap-3">
          <Link
            to="/fordon"
            className="btn-cta btn-cta-accent group inline-flex items-center gap-2 px-8 py-4 text-base font-semibold shadow-[0_20px_60px_-20px_hsl(var(--accent)/0.7)]"
          >
            Visa alla <span suppressHydrationWarning>{items.length}</span> fordon
            <ArrowRight size={18} className="transition group-hover:translate-x-1" />
          </Link>
          <p className="text-xs text-muted-foreground" suppressHydrationWarning>
            {hiddenCount} fler i lagret · uppdateras dagligen
          </p>
        </div>
      )}

    </>
  );
}

export function FyndanuGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-3xl border border-border bg-card"
        >
          <div className="aspect-[4/3] animate-pulse bg-secondary" />
          <div className="space-y-2 p-5">
            <div className="h-5 w-3/4 animate-pulse rounded bg-secondary" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-secondary" />
          </div>
        </div>
      ))}
    </div>
  );
}
